/**
 * mc-cart-discount.js — Manifesto
 *
 * Aplicação/remoção de cupom de desconto direto na gaveta lateral
 * (snippets/cart-drawer.liquid → <mc-cart-discount>).
 *
 * Princípio (task cart-drawer-discount-code, RF3): a Shopify é a fonte de verdade
 * do valor descontado. NÃO recalculamos preço em JS. O fluxo é:
 *
 *   1. APLICAR  → GET /discount/{CODE}  (grava o discount code no carrinho)
 *   2. VALIDAR  → GET /cart.js          (lê cart.discount_codes[].applicable)
 *   3. RENDER   → GET ?sections=cart-drawer,cart-icon-bubble  (re-render server-side;
 *                 Total, linha de desconto, "Finalizar — R$" e PIX vêm já recalculados
 *                 a partir de cart.total_price). Reusa a MESMA trilha do cart-drawer.js.
 *
 * Hooks do Dawn preservados: só trocamos o innerHTML de `.cart-drawer__footer` e de
 * `#cart-icon-bubble` — nunca de <cart-drawer>, #CartDrawer-Form, <cart-drawer-items>,
 * <quantity-input>, etc. O bloco de cupom vive dentro do footer, então é recriado
 * (e re-vinculado) a cada re-render.
 *
 * DP1 (fechado): cupom COMBINA com PIX (stack). Os totais derivam de
 * cart.total_price (já líquido do cupom) — sem cálculo de preço em JS.
 *
 * ⚠ DP3 / Spike F1: o contrato de aplicar (/discount/{code}) é estável. A REMOÇÃO não
 * tem AJAX oficial de "clear"; usamos a semântica de que /discount/{lista} REDEFINE o
 * conjunto de códigos do carrinho — para remover, re-aplicamos os códigos restantes
 * (vazio = limpa). Confirmar em loja real e ajustar APPLY_PATH/clear se necessário.
 *
 * Correção pós-review (task 0003, NO-GO B3): após aplicar/remover cupom com
 * sucesso, publicamos PUB_SUB_EVENTS.cartUpdate (source 'mc-cart-discount')
 * — sem isso, o widget de frete do carrinho (mc-shipping-calc.js, contexto
 * 'cart') na OUTRA superfície (ex.: cupom aplicado na gaveta, mas o Total
 * estimado da página /cart aberta em outra aba/instância) não teria como
 * saber que cart.total_price mudou. Verificado que isso não tem side effects
 * indesejados: cart-auto-open.js só reage a source em
 * ['product-form','mc-quick-add','quick-add'] (ADD_SOURCES) — 'mc-cart-discount'
 * não abre a gaveta nem duplica telemetria. product-form.js já publica
 * cartUpdate E chama cart.renderContents() diretamente (mesmo padrão aqui:
 * publish + renderSections() próprio) — não é um padrão novo no tema.
 */

(function () {
  'use strict';

  // Task 0007: <mc-cart-discount> passa a existir em duas superfícies
  // possíveis (gaveta global + página /cart, main-cart-footer.liquid).
  // Guard de idempotência — evita NotSupportedError se o asset for
  // avaliado 2x (ex.: <cart-drawer> global + algum carregamento futuro
  // do mesmo script direto na página).
  if (customElements.get('mc-cart-discount')) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Task 0007 (correção pós-review): a Section Rendering API identifica
  // sections pelo `data-id` real (id da instância da section no template),
  // NÃO pelo nome do arquivo/tipo — igual ao cart.js nativo do Dawn, que
  // resolve `document.getElementById('main-cart-footer').dataset.id` (ver
  // theme/assets/cart.js, getSectionsToRender()). Usar o literal
  // 'main-cart-footer' aqui devolvia um fragmento órfão (a chave do JSON de
  // resposta é o id de instância, não bate com o literal) e o breakdown na
  // /cart nunca atualizava sem F5.
  //
  // Fora da /cart, #main-cart-footer é null e o comportamento fica IDÊNTICO
  // ao anterior (só cart-drawer,cart-icon-bubble) — a gaveta não regride.
  function getSections() {
    var base = 'cart-drawer,cart-icon-bubble';
    var footerEl = document.getElementById('main-cart-footer');
    if (footerEl && footerEl.dataset.id) {
      base += ',' + footerEl.dataset.id;
    }
    return base;
  }

  // Task 0008 (S1/DP1): a mensagem de sucesso deixa de depender de uma
  // variável de módulo única (pendingMessage) consumida pelo connectedCallback
  // do PRÓXIMO elemento a montar — essa corrida deixava a mensagem "grudada"
  // só na primeira superfície re-renderizada (a gaveta, por rodar primeiro em
  // renderSections()), nunca na página /cart. Agora cada swap escreve o
  // feedback diretamente no container que acabou de trocar (showFeedbackOn),
  // então gaveta e /cart recebem sua própria mensagem, cada uma no seu swap.

  // Escreve a mensagem de sucesso/erro no [data-mc-discount-feedback] de UM
  // container específico (gaveta OU /cart) — chamado após cada innerHTML-swap
  // em renderSections(), nunca de conectedCallback (AJ-5: feedback só no
  // sucesso de apply()/remove(), nunca em re-render não relacionado, ex.:
  // qty change, add-to-cart, disclosure da 0006).
  function showFeedbackOn(target, msg) {
    if (!target || !msg) return;
    var el = target.querySelector('[data-mc-discount-feedback]');
    if (!el) return;
    el.textContent = msg.text;
    el.classList.remove('is-error', 'is-success');
    el.classList.add(msg.type === 'error' ? 'is-error' : 'is-success');
  }

  // Restaura o foco no input de cupom após o swap — só chamado para a
  // superfície que INICIOU a ação (AJ-4). `preventScroll` evita scroll jump
  // na /cart (a gaveta é posição fixa, não afetada de qualquer forma).
  function focusInputOn(target) {
    if (!target) return;
    var input = target.querySelector('[data-mc-discount-input]');
    if (input) {
      try { input.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  // Identifica em qual superfície um elemento vive — usado para saber onde
  // restaurar o foco (AJ-4) sem depender da instância antiga de
  // <mc-cart-discount>, que é destruída pelo próprio swap.
  function surfaceOf(el) {
    if (!el) return null;
    if (el.closest('#CartDrawer')) return 'drawer';
    if (el.closest('#main-cart-footer')) return 'cart';
    return null;
  }

  function eqCode(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  }

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  // Aplica um conjunto de códigos via /discount/{lista}. O endpoint redireciona;
  // pedimos o redirect para a raiz e descartamos o corpo.
  function applyCodes(codesString) {
    var url = ROOT + 'discount/' + encodeURIComponent(codesString) + '?redirect=' + encodeURIComponent(ROOT);
    return fetch(url, { headers: { Accept: 'text/html' }, credentials: 'same-origin' });
  }

  // Redefine o conjunto de cupons do carrinho para `codes` (array). Vazio ⇒ limpa tudo.
  // ⚠ Spike F1 (DP3): /discount/ com lista VAZIA não limpa o último cupom — por isso
  // o clear usa /cart/update.js {discount:''}. Nenhum dos dois é API oficial documentada;
  // confirmar ambos em loja real antes de produção e ajustar aqui se necessário.
  function setCodes(codes) {
    if (codes.length) return applyCodes(codes.join(','));
    return fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: '' }),
    });
  }

  function fetchCart() {
    return getJSON(ROOT + 'cart.js');
  }

  // Notifica outras superfícies (ex.: mc-shipping-calc.js contexto 'cart' na
  // /cart, quando o cupom foi aplicado/removido pela gaveta) de que o
  // carrinho mudou. Guardado defensivamente — pub/sub do Dawn pode não estar
  // carregado ainda em algum cenário incomum, e telemetria/publish nunca deve
  // travar o fluxo de cupom.
  function publishCartUpdate() {
    if (typeof window.publish !== 'function') return;
    try {
      var eventName = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
      window.publish(eventName, { source: 'mc-cart-discount' });
    } catch (err) {
      // Nunca deve travar o fluxo de cupom.
    }
  }

  // Códigos aplicáveis hoje no carrinho (ignora os applicable:false residuais).
  function applicableCodes(cart) {
    return (cart.discount_codes || [])
      .filter(function (d) { return d.applicable; })
      .map(function (d) { return d.code; });
  }

  // Re-render server-side reutilizando a Section Rendering API (mesma trilha do Dawn).
  //
  // `message` (Task 0008/S1): {text,type} do sucesso de apply()/remove() a
  // escrever no feedback de CADA superfície recém-trocada (AJ-1). `undefined`
  // em qualquer outro chamador futuro é seguro (showFeedbackOn no-ops).
  //
  // `initiatorSurface` (Task 0008/AJ-4): 'drawer' | 'cart' | null — só essa
  // superfície recebe foco de volta no input; a outra nunca move foco.
  function renderSections(message, initiatorSurface) {
    // Captura o id de instância ANTES do fetch (mesmo elemento que getSections()
    // vai inspecionar) — é a chave real dentro do JSON de resposta, não o literal
    // 'main-cart-footer'. Null fora da /cart (no-op abaixo).
    var footerEl = document.getElementById('main-cart-footer');
    var footerSectionId = footerEl && footerEl.dataset.id;

    return getJSON(ROOT + '?sections=' + getSections()).then(function (data) {
      var parser = new DOMParser();

      // Footer da gaveta — troca só o innerHTML, preserva os hooks do Dawn ao redor.
      if (data['cart-drawer']) {
        var cd = parser.parseFromString(data['cart-drawer'], 'text/html');
        var newFooter = cd.querySelector('.cart-drawer__footer');
        var curFooter = document.querySelector('#CartDrawer .cart-drawer__footer');
        if (newFooter && curFooter) {
          curFooter.innerHTML = newFooter.innerHTML;
          showFeedbackOn(curFooter, message);
          if (initiatorSurface === 'drawer') focusInputOn(curFooter);
        }
      }

      // Botão de carrinho do header (subtotal reflete cart.total_price líquido do cupom).
      if (data['cart-icon-bubble']) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          var bd = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
          var src = bd.querySelector('#cart-icon-bubble') || bd.querySelector('.shopify-section');
          if (src) bubble.innerHTML = src.innerHTML;
        }
      }

      // Task 0007: card "Resumo do pedido" na página /cart — só existe nessa
      // página (main-cart-footer.liquid), no-op em qualquer outro lugar. Chave
      // de acesso é o id de INSTÂNCIA da section (footerSectionId), não o
      // literal 'main-cart-footer'. O .js-contents do bloco `subtotal` é o
      // único nessa section (o bloco `buttons` fica fora dele), então o
      // seletor abaixo é inambíguo.
      if (footerSectionId && data[footerSectionId]) {
        var mf = parser.parseFromString(data[footerSectionId], 'text/html');
        var newSummaryContents = mf.querySelector('#main-cart-footer .js-contents');
        var curSummaryContents = document.querySelector('#main-cart-footer .js-contents');
        if (newSummaryContents && curSummaryContents) {
          curSummaryContents.innerHTML = newSummaryContents.innerHTML;
          showFeedbackOn(curSummaryContents, message);
          if (initiatorSurface === 'cart') focusInputOn(curSummaryContents);
        }
      }
    });
  }

  customElements.define(
    'mc-cart-discount',
    class McCartDiscount extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-mc-discount-form]');
        this.input = this.querySelector('[data-mc-discount-input]');
        this.applyBtn = this.querySelector('[data-mc-discount-apply]');
        this.feedback = this.querySelector('[data-mc-discount-feedback]');

        // Strings vêm do locale (pt-BR.default.json) via data-* no markup — fonte única.
        var d = this.dataset;
        this.strings = {
          applied: d.strApplied || 'Cupom %CODE% aplicado',
          applying: d.strApplying || 'Aplicando…',
          removed: d.strRemoved || 'Cupom removido',
          invalid: d.strInvalid || 'Cupom inválido ou expirado.',
          notApplicable: d.strNotApplicable || 'Este cupom não se aplica ao seu carrinho.',
          error: d.strError || 'Não foi possível aplicar o cupom. Tente novamente.',
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.onClick = this.onClick.bind(this);
        if (this.form) this.form.addEventListener('submit', this.onSubmit);
        this.addEventListener('click', this.onClick);

        // Task 0008 (AJ-5): feedback de sucesso e foco NÃO são mais lidos aqui.
        // connectedCallback roda em TODO re-render do elemento (qty change,
        // add-to-cart, disclosure da 0006, e também no swap do próprio
        // apply()/remove()) — escrever feedback/foco aqui vazava para
        // re-renders não relacionados ao cupom. renderSections() agora chama
        // showFeedbackOn()/focusInputOn() diretamente no container recém-
        // trocado, só no sucesso de apply()/remove().
      }

      onSubmit(e) {
        e.preventDefault();
        var code = this.input ? this.input.value.trim() : '';
        if (!code) return;
        this.apply(code);
      }

      onClick(e) {
        var btn = e.target.closest('[data-mc-discount-remove]');
        if (!btn) return;
        e.preventDefault();
        this.remove(btn.getAttribute('data-code'));
      }

      setLoading(on) {
        if (this.applyBtn) {
          this.applyBtn.disabled = on;
          this.applyBtn.classList.toggle('is-loading', on);
          // O texto do botão fica visualmente oculto (spinner) — anuncia o estado p/ leitores de tela.
          if (on) this.applyBtn.setAttribute('aria-label', this.strings.applying);
          else this.applyBtn.removeAttribute('aria-label');
        }
        if (this.input) this.input.disabled = on;
        this.querySelectorAll('[data-mc-discount-remove]').forEach(function (b) { b.disabled = on; });
      }

      clearFeedback() {
        if (this.feedback) {
          this.feedback.textContent = '';
          this.feedback.classList.remove('is-error', 'is-success');
        }
      }

      showFeedback(text, type) {
        if (!this.feedback) return;
        this.feedback.textContent = text;
        this.feedback.classList.remove('is-error', 'is-success');
        this.feedback.classList.add(type === 'error' ? 'is-error' : 'is-success');
      }

      apply(code) {
        var self = this;
        // Task 0008 (AJ-4): capturada ANTES do swap — self é destruído pelo
        // innerHTML-swap da própria superfície, então precisamos saber agora
        // onde restaurar o foco depois.
        var initiatorSurface = surfaceOf(self);
        self.setLoading(true);
        self.clearFeedback();

        applyCodes(code)
          .then(fetchCart)
          .then(function (cart) {
            var entry = (cart.discount_codes || []).find(function (d) { return eqCode(d.code, code); });
            if (!entry || entry.applicable === false) {
              // Não deixa cupom inválido grudado: re-aplica só os que já eram aplicáveis.
              // AJ-3: caminho de erro inalterado — via showFeedback direto em self,
              // sem renderSections(), visível só na superfície onde o usuário digitou.
              var keep = applicableCodes(cart).filter(function (c) { return !eqCode(c, code); });
              return setCodes(keep).then(function () {
                self.setLoading(false);
                self.showFeedback(entry ? self.strings.notApplicable : self.strings.invalid, 'error');
              });
            }
            var message = { text: self.strings.applied.replace('%CODE%', entry.code), type: 'success' };
            return renderSections(message, initiatorSurface).then(publishCartUpdate);
          })
          .catch(function () {
            self.setLoading(false);
            self.showFeedback(self.strings.error, 'error');
          });
      }

      remove(code) {
        var self = this;
        var initiatorSurface = surfaceOf(self);
        self.setLoading(true);
        self.clearFeedback();

        fetchCart()
          .then(function (cart) {
            var keep = applicableCodes(cart).filter(function (c) { return !eqCode(c, code); });
            return setCodes(keep);
          })
          .then(function () {
            var message = { text: self.strings.removed, type: 'success' };
            return renderSections(message, initiatorSurface).then(publishCartUpdate);
          })
          .catch(function () {
            self.setLoading(false);
            self.showFeedback(self.strings.error, 'error');
          });
      }
    }
  );
})();
